<script setup>
import useApi from '@/composables/useApi';
import useFiles from '@/composables/useFiles';
import useToast from '@/composables/useToast';
import useToolModels from '@/composables/useToolModels';
import Btn from '../buttons/Btn.vue';
import TextViewer from './TextViewer.vue';
import Progress from '../Progress.vue';
import { computed, ref } from 'vue';
import TextGenPrompt from './TextGenPrompt.vue';

const { askAi, cost, costReadable, isWorking } = useApi();
const { downloadTextAsFile, generateExcel } = useFiles();
const { toast } = useToast();
const { toolModels } = useToolModels();

let id = 0, order = 0;
const gptVersion = ref('gpt-4o-mini');
const promptsGigaArray = ref([{ requests: [], id: id++, mode: 'one', error: '' }]);
const outputMode = ref('html');
const htmlResults = ref([]);
const txtResults = ref([]);
const progress = ref(0);
const totalRequests = ref(0);

const isError = computed(() => promptsGigaArray.value.some(el => el.error));

function addReq() {
  promptsGigaArray.value.push({ requests: [], id: id++, mode: 'one', error: '' });
}

function delReq(id) {
  if (confirm('Действительно удалить данный промпт?'))
    promptsGigaArray.value = promptsGigaArray.value.filter(el => el.id != id);
}

async function gen() {
  const startTime = performance.now();
  cost.value = 0;
  const streamCount = 4
  let currentlyLaunched = 0
  const wait = async (x = 1) => new Promise(r => { const i = setInterval(() => currentlyLaunched < x && (clearInterval(i), r()), 50) })


  htmlResults.value = [];
  txtResults.value = [];
  totalRequests.value = promptsGigaArray.value.reduce((sum, p) => sum + p.requests.length, 0);
  progress.value = 0;
  if (!promptsGigaArray.value.length) return alert('Не указано ни одного промта');
  for (let i in promptsGigaArray.value) {
    if (promptsGigaArray.value[i].error) return alert(`Ошибка в блоке ${i}: ${promptsGigaArray.value[i].error}`);
  }

  isWorking.value = true;
  try {
    order = 0
    for (let prompt of promptsGigaArray.value) {
      for (let reqObj of prompt.requests) {
        let request = reqObj.request;
        if (currentlyLaunched >= streamCount) await wait(streamCount)
        currentlyLaunched++
        const current_order = ++order
        askAi(request, gptVersion.value, { tries: 3, timeout_seconds: 120, addcost: true }).then(res => {
          if (typeof res.answer != 'string') {
            let err = request.substring(0, 50) + (request.length > 50 ? '...' : '');
            toast.error(`Ошибка при запросе ${err} - пропущен`);
            return; // Skip this request, continue with others
          }
          let text = res.answer;
          if (outputMode.value === 'html') {
            if (text.match(/```html/)) {
              const match = text.match(/```html\s([\s\S]*?)\s```/);
              if (match) text = match[1].trim();
            }
            if (text.match(/<body>/i)) {
              text = text.match(/<body>([\s\S]*?)<\/body>/)[1].trim();
            }
            htmlResults.value.push(...splitHTMLByHeaders(text, current_order));
          } else {
            txtResults.value.push({ id: id++, text, prompt, reqObj, order: current_order });
          }
        }).finally(() => { currentlyLaunched--; progress.value++; })
      }
    }
  } catch (gigaErr) {
    alert(`Ошибка ${gigaErr}, дальшая работа прервана`);
  } finally {
    await wait()
    if (outputMode.value === 'html') {
      htmlResults.value = htmlResults.value.toSorted((a, b) => {
        const v = a.order - b.order
        return v ? v : a.localOrder - b.localOrder
      });
      console.log(htmlResults.value)
    } else {
      txtResults.value = txtResults.value.toSorted((a, b) => a.order - b.order);
    }
    isWorking.value = false;
    const endTime = performance.now();
    progress.value = 0;
    totalRequests.value = 0;
    console.log(`Generation finished in ${(endTime - startTime).toFixed(2)} ms`);
  }
}

function downloadHtml() {
  let v = htmlResults.value.map(el => el.text).join('\n');
  downloadTextAsFile(`textgen_${new Date().toLocaleDateString('ru-RU')}_.html`, v, { texttype: 'html' });
}

function downloadTxt() {
  let text = txtResults.value.map(r => r.text).join('\n\n---\n\n');
  downloadTextAsFile(`textgen_${new Date().toLocaleDateString('ru-RU')}_.txt`, text);
}

function downloadXlsx() {
  let rows = txtResults.value.map(result =>
    result.prompt.mode === 'one'
      ? [result.reqObj.request, result.text]
      : [result.reqObj.theme, result.text]
  );
  generateExcel('textgen', 'Sheet1', rows);
}

function splitHTMLByHeaders(html, current_order) {
  const regex = /(<h[12][^>]*>.*?<\/h[12]>)/gi;
  const headers = html.split(regex).map(el => el.trim()).filter(Boolean);
  const chunks = [];
  let currentChunk = '';

  headers.forEach(part => {
    if (/<h[12][^>]*>/.test(part)) {
      if (currentChunk) chunks.push({ text: currentChunk, id: id++, order: current_order });
      currentChunk = part;
    } else {
      currentChunk += currentChunk ? '\n' + part : part;
    }
  });

  if (currentChunk) chunks.push({ text: currentChunk, id: id++, order: current_order });
  for (let i in chunks)
    chunks[i].localOrder = i

  return chunks;
}

async function updateText(chunk, event) {
  const { prompt, selection, selectionStart } = event;
  let res = await askAi(prompt, gptVersion.value, { tries: 3, timeout_seconds: 120, addcost: true });
  if (typeof res.answer != 'string') {
    alert('Ошибка при обновлении текста');
  } else {
    let text = res.answer;
    if (outputMode.value === 'html' && text.match(/```html/)) {
      const match = text.match(/```html\s([\s\S]*?)\s```/);
      if (match) text = match[1].trim();
    }
    if (!selection) {
      chunk.text = text || chunk.text;
    } else {
      let oldPart = chunk.text.substring(selectionStart, selectionStart + selection.length);
      let split = chunk.text.split(oldPart);
      chunk.text = split[0] + text + split[1];
    }
  }
}
</script>

<template>
  <form @submit.prevent="gen" class="toolsprovider__form" :disabled="isWorking">
    <h1 class="toolsprovider__title">Генерировать текст на заданную тему</h1>
    <div class="toolsprovider__text">
      <label><input type="radio" v-model="outputMode" value="html"> HTML mode</label>
      <label><input type="radio" v-model="outputMode" value="txt"> TXT mode</label>
    </div>
    <TextGenPrompt v-for="par, index in promptsGigaArray" :key="par.id" v-model:mode="promptsGigaArray[index].mode"
      v-model:requests="promptsGigaArray[index].requests" @delete="() => delReq(par.id)"
      v-model:error="promptsGigaArray[index].error" />
    <Btn color="green" class="toolsprovider__btn w100" @click.stop.prevent="addReq" :small="true">Добавить запрос</Btn>
    <select class="toolsprovider__input toolsprovider__input_select" v-model="gptVersion">
      <option v-for="model in toolModels" :key="model.value" :value="model.value">{{ model.label }}</option>
    </select>
    <p v-if="cost" class="toolsprovider__text">Примерная стоимость: {{ costReadable }}</p>
    <Progress v-if="isWorking && totalRequests > 0" :value="progress" :max="totalRequests" />
    <Btn color="dark" :disabled="isWorking || isError" class="toolsprovider__btn">
      <template v-if="isWorking">
        <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="20" stroke-width="8" stroke="#007bff"
            stroke-dasharray="31.41592653589793 31.41592653589793" fill="none" stroke-linecap="round">
            <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="1.25s"
              keyTimes="0;1" values="0 50 50;360 50 50"></animateTransform>
          </circle>
        </svg>
        <span>Генерирую...</span>
      </template>
      <span v-else>Генерировать</span>
    </Btn>
  </form>
  <div v-if="outputMode === 'html'">
    <TextViewer v-for="result in htmlResults" :key="result.id" v-model:text="result.text"
      @sendPrompt="(e) => updateText(result, e)" />
  </div>
  <div v-if="outputMode === 'txt'">
    <TextViewer v-for="result in txtResults" :key="result.id" v-model:text="result.text" textOnly
      @sendPrompt="(e) => updateText(result, e)" />
  </div>
  <div class="toolsprovider__stickdown" v-if="outputMode === 'html' ? htmlResults.length : txtResults.length">
    <p v-if="cost" class="toolsprovider__text">Примерная стоимость: {{ costReadable }}</p>
    <template v-if="outputMode === 'html'">
      <Btn :disabled="isWorking" color="green" class="toolsprovider__btn w100" @click="downloadHtml">Скачать HTML</Btn>
    </template>
    <div v-else style="display:flex; gap: 10px; justify-content: space-between">
      <Btn :disabled="isWorking" color="green" class="toolsprovider__btn" style="width: calc(50% - 5px);"
        @click="downloadTxt">Скачать TXT</Btn>
      <Btn :disabled="isWorking" color="green" class="toolsprovider__btn" style="width: calc(50% - 5px);"
        @click="downloadXlsx">Скачать XLSX</Btn>
    </div>
  </div>
</template>
<style></style>